import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { auth } from "./api";

const AuthContext = createContext(null);

function readStoredUser() {
  try {
    const raw = localStorage.getItem("mie_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readStoredUser());
  const [booted, setBooted] = useState(false);

  // If we have a stored token, validate it via /me on first load.
  useEffect(() => {
    const token = localStorage.getItem("mie_token");
    if (token && !user) {
      auth.me().then(setUser).catch(() => {
        localStorage.removeItem("mie_token");
        localStorage.removeItem("mie_user");
      }).finally(() => setBooted(true));
    } else {
      setBooted(true);
    }
  }, []); // eslint-disable-line

  async function login(email, password) {
    const r = await auth.login(email, password);
    localStorage.setItem("mie_token", r.access_token);
    localStorage.setItem("mie_user", JSON.stringify(r.user));
    setUser(r.user);
    return r.user;
  }

  function logout() {
    localStorage.removeItem("mie_token");
    localStorage.removeItem("mie_user");
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, booted }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function RequireAuth({ children, role }) {
  const { user, booted } = useAuth();
  const location = useLocation();
  if (!booted) return <div className="page"><p className="subtle">Loading…</p></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (role && user.role !== role) {
    // Wrong role — send them to their own landing.
    const dest = user.role === "agency_admin" ? "/portfolio" : `/my/${user.client_id}`;
    return <Navigate to={dest} replace />;
  }
  return children;
}

export function DefaultLanding() {
  const { user, booted } = useAuth();
  if (!booted) return <div className="page"><p className="subtle">Loading…</p></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "agency_admin") return <Navigate to="/portfolio" replace />;
  if (user.client_id) return <Navigate to={`/my/${user.client_id}`} replace />;
  return <Navigate to="/login" replace />;
}
